/**
 * Column definitions for the offer workspace's merged table (`offer-unified-table`
 * capability, CU-86cbc3r8b) — the surface retained at the Nośniki tab/address,
 * now carrying unit, cost, price, and decision columns in one table. Columns
 * matching OfferUnitListItemSchema fields only.
 */
import * as React from 'react';
import { cn } from '@ooh/ui/lib/utils';
import { onKeyDownCommitOnEnter } from '@ooh/ui/lib/keyboard';
import { Checkbox } from '@ooh/ui/components/checkbox';
import { Input } from '@ooh/ui/components/input';
import type { ColumnDef } from '@ooh/ui/components/data-table';
import { INTERACTIVE_CELL_CLASSNAME } from '@ooh/ui/components/data-table';
import { useColumnPreferences } from '@ooh/ui/components/data-table-column-preferences';
import type { OfferUnitListResponse } from '@ooh/contracts';
import type { TFunction } from 'i18next';
import { buildUnitsSharedColumns } from './units-shared-fields';
import { renderMediaUnitGrwId } from '../../../lib/media-unit-field-renderers';
import {
  buildOfferUnitCostColumns,
  OFFER_UNIT_COST_COLUMN_IDS,
} from './lib/offer-unit-cost-columns';
import {
  buildOfferUnitPriceColumns,
  OFFER_UNIT_PRICE_OVERRIDE_COLUMN_IDS,
  type OfferUnitPriceColumnConfig,
} from './lib/offer-unit-price-columns';
// Cross-feature import of the catalog's own default-hidden-column set — an
// existing precedent (see `docs/WEB_ARCHITECTURE.md:36` convention-gap
// finding), needed here so the merged table can seed its layout from the
// user's actual Katalog nośników choices on first visit.
import { DEFAULT_HIDDEN_COLUMNS as CATALOG_DEFAULT_HIDDEN_COLUMNS } from '@ooh/screens';

/** The catalog's own column-preference storage prefix (`use-media-units-page.ts`). */
const CATALOG_COLUMN_STORAGE_PREFIX = 'ooh:inventory';

export type OfferUnitItem = OfferUnitListResponse[number];

export const UNITS_TABLE_SORT_KEYS = [
  'sortOrder',
  'mediaType',
  'format',
  'supplier',
  'supplierNumber',
  'grwId',
  'region',
  'county',
  'agglomeration',
  'city',
  'address',
  'dimensions',
  'audience',
  'notes',
  'wfContactPerson',
  'locationDescription',
  'illuminated',
  'material',
  'multiformat',
  'tags',
  'roadNumber',
  'area',
  'note',
  'clientDecision',
  // ─── Pricing sort keys (offer-unified-table merge, CU-86cbc3r8b) ───
  'clientPriceNet',
  'discountPercent',
  'lineTotal',
  // ─── Cost sort keys (offer-unified-table merge, CU-86cbc3r8b) — must match
  // COST_RANGE_DIMENSIONS ids verbatim. A cost sort is refused server-side
  // unless the caller's cost grant is unrestricted (task 2.1). ───
  'catalogPrice',
  'rent',
  'exposureCost',
  'additionalCosts',
  'discountedPrice',
  'lastMinutePrice',
  'starterPrice',
  'productionCost',
  'installationCost',
  'dismantlingCost',
  'clientProduction',
  'clientInstallation',
  'clientDismantling',
  'clientAdditionalFees',
] as const;
export type UnitsTableSortKey = (typeof UNITS_TABLE_SORT_KEYS)[number];

/**
 * localStorage namespace for the merged offer table's column visibility/order
 * preferences — replaces the retired 'ooh:offer-units'/'ooh:offer-pricing'
 * namespaces with one shared namespace, fully independent of the catalog's
 * `ooh:inventory:*` keys and of Decyzje klienta's `ooh:offer-decisions:*` keys.
 */
export const OFFER_TABLE_COLUMN_STORAGE_PREFIX = 'ooh:offer-table';

/**
 * The row-selection checkbox always leads the column order and is excluded from
 * the "Kolumny" toggle list. It is absent for view-only users;
 * `useColumnPreferences` gracefully ignores a pinned id that is absent.
 */
export const OFFER_TABLE_PINNED_COLUMN_IDS = ['checkbox'];

/** Feature columns whose default visibility is stated explicitly (never inherited from the catalog). */
const OFFER_TABLE_DEFAULT_VISIBLE_FEATURE_IDS: readonly string[] = [
  'clientPriceNet',
  'discountPercent',
  'lineTotal',
  'clientDecision',
  'note',
  'identifier',
];

/**
 * Wires the shared `useColumnPreferences` hook for the merged offer table
 * under its own storage namespace, so hiding/reordering columns here never
 * affects the catalog's own `ooh:inventory:*` preferences nor Decyzje
 * klienta's `ooh:offer-decisions:*` preferences.
 */
export function useOfferTableColumnPreferences(allColumnIds: string[]) {
  return useColumnPreferences({
    storageKeyPrefix: OFFER_TABLE_COLUMN_STORAGE_PREFIX,
    allColumnIds,
    pinnedIds: OFFER_TABLE_PINNED_COLUMN_IDS,
  });
}

function readJsonArray(key: string): string[] | null {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    const parsed: unknown = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : null;
  } catch {
    return null;
  }
}

function readJsonRecord(key: string): Record<string, number> | null {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    const parsed: unknown = JSON.parse(stored);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;
    return parsed as Record<string, number>;
  } catch {
    return null;
  }
}

/**
 * Seeds the merged table's column layout from the user's current Katalog
 * nośników layout on first visit — a plain synchronous function, called
 * before/alongside `useOfferTableColumnPreferences`'s own `useState` lazy
 * initializer runs, so the very first render is already seeded (a
 * `useEffect`-based seed would fire one render too late).
 *
 * No-ops once an `ooh:offer-table:hiddenColumns` key already exists — the
 * two layouts are independent after the first visit. `catalogFieldIds` is
 * the merged table's own set of catalogue-sourced column ids (its shared
 * `unified`-surface columns) — only those ids are copied from the catalog's
 * layout; feature-only columns (price/discount/lineTotal/decision/note/
 * every cost column) are never read from the catalog, since it has no
 * equivalent slot for them.
 */
export function seedOfferTableColumnPreferencesFromCatalog(catalogFieldIds: string[]): void {
  const hiddenKey = `${OFFER_TABLE_COLUMN_STORAGE_PREFIX}:hiddenColumns`;
  try {
    if (localStorage.getItem(hiddenKey) !== null) return;

    const catalogFieldIdSet = new Set(catalogFieldIds);
    const catalogHidden = readJsonArray(`${CATALOG_COLUMN_STORAGE_PREFIX}:hiddenColumns`) ?? [
      ...CATALOG_DEFAULT_HIDDEN_COLUMNS,
    ];
    const seededHidden = new Set(catalogHidden.filter((id) => catalogFieldIdSet.has(id)));
    // Every cost column starts hidden regardless of what the catalogue shows
    // for costs (offer-unified-table spec: "whatever the catalogue shows for
    // costs") — costs are internal margin information, not the default
    // reading of an offer. The nine formatka price-override columns
    // (offer-unit-price-columns.tsx) keep the same default-hidden treatment
    // they had while they lived among the cost columns, even though they are
    // now editable — unchanged default-visibility behavior.
    for (const id of OFFER_UNIT_COST_COLUMN_IDS) seededHidden.add(id);
    for (const id of OFFER_UNIT_PRICE_OVERRIDE_COLUMN_IDS) seededHidden.add(id);
    // Feature columns (price/discount/lineTotal/decision/note/identifier)
    // never inherit hidden state from the catalog — enforced here
    // defensively so OFFER_TABLE_DEFAULT_VISIBLE_FEATURE_IDS's own doc
    // comment stays true even if a future catalogFieldIds composition ever
    // includes one of these ids (requirements-sweep fix).
    for (const id of OFFER_TABLE_DEFAULT_VISIBLE_FEATURE_IDS) seededHidden.delete(id);
    localStorage.setItem(hiddenKey, JSON.stringify([...seededHidden]));

    // Column order: only copy when the user has an explicit catalog order —
    // absent that, the merged table's own natural order already matches the
    // catalogue's default relative order (both derive from the same registry),
    // so leaving the order key unset produces the correct starting point.
    const catalogOrder = readJsonArray(`${CATALOG_COLUMN_STORAGE_PREFIX}:columnOrder`);
    if (catalogOrder) {
      const seededOrder = catalogOrder.filter((id) => catalogFieldIdSet.has(id));
      localStorage.setItem(
        `${OFFER_TABLE_COLUMN_STORAGE_PREFIX}:columnOrder`,
        JSON.stringify(seededOrder),
      );
    }

    const catalogWidths = readJsonRecord(`${CATALOG_COLUMN_STORAGE_PREFIX}:columnWidths`);
    if (catalogWidths) {
      const seededWidths = Object.fromEntries(
        Object.entries(catalogWidths).filter(([id]) => catalogFieldIdSet.has(id)),
      );
      if (Object.keys(seededWidths).length > 0) {
        localStorage.setItem(
          `${OFFER_TABLE_COLUMN_STORAGE_PREFIX}:columnWidths`,
          JSON.stringify(seededWidths),
        );
      }
    }
  } catch {
    // localStorage may be unavailable; silently ignore (matches
    // useColumnPreferences' own storage-unavailable fallback).
  }
}

interface NoteInputProps {
  unit: OfferUnitItem;
  onBlur: (unitId: OfferUnitItem['id'], note: string) => void;
  disabled?: boolean;
  placeholder: string;
  ariaLabel: string;
}

function NoteInput({ unit, onBlur, disabled, placeholder, ariaLabel }: NoteInputProps) {
  const [value, setValue] = React.useState(unit.note ?? '');

  React.useEffect(() => {
    setValue(unit.note ?? '');
  }, [unit.note]);

  if (disabled) {
    // Read-only: render as plain text so view-only users cannot interact with the field.
    return (
      <span className="w-full text-[12px] text-muted-foreground/80" aria-label={ariaLabel}>
        {value || ''}
      </span>
    );
  }

  return (
    <Input
      className={cn(
        'h-auto bg-transparent text-[12px] shadow-none outline-none border-transparent focus-visible:ring-0 placeholder:text-muted-foreground/60 px-0 py-0',
      )}
      value={value}
      placeholder={placeholder}
      aria-label={ariaLabel}
      data-testid={`unit-note-input-${unit.id}`}
      onChange={(e) => setValue(e.target.value)}
      onBlur={() => onBlur(unit.id, value)}
      onKeyDown={onKeyDownCommitOnEnter}
    />
  );
}

interface ColumnFactoryOptions {
  canEdit: boolean;
  selectedIds: Set<OfferUnitItem['id']>;
  onCheckedChange: (id: OfferUnitItem['id'], checked: boolean) => void;
  onNoteBlur: (unitId: OfferUnitItem['id'], note: string) => void;
  canViewSupplierDetails: boolean;
  /** Price/discount edit wiring — omitted (or `canEdit: false`) renders read-only price cells. */
  price: Omit<OfferUnitPriceColumnConfig, 't'>;
  t: TFunction<'offers'>;
}

/** Column ids sourced from `buildUnitsSharedColumns` (the catalogue-registry-derived `unified` surface) plus `identifier`. */
export function getOfferTableCatalogColumnIds(
  canViewSupplierDetails: boolean,
  t: TFunction<'offers'>,
): string[] {
  return ['identifier', ...buildUnitsSharedColumns(t, canViewSupplierDetails).map((c) => c.id)];
}

export function buildUnitsTabColumns({
  canEdit,
  selectedIds,
  onCheckedChange,
  onNoteBlur,
  canViewSupplierDetails,
  price,
  t,
}: ColumnFactoryOptions): ColumnDef<OfferUnitItem>[] {
  const cols: ColumnDef<OfferUnitItem>[] = [];

  if (canEdit) {
    cols.push({
      id: 'checkbox',
      label: t('workspace.unitsTab.columns.checkbox'),
      align: 'center',
      width: 36,
      resizable: false,
      // Structural column, pinned via OFFER_TABLE_PINNED_COLUMN_IDS — not user-reorderable.
      fixed: true,
      headerClassName: 'px-1',
      // INTERACTIVE_CELL_CLASSNAME ('relative z-[1]') so this checkbox keeps
      // winning hit-tests over the row-link stretched-link overlay — see data-table-helpers.tsx.
      cellClassName: `px-1 py-2 ${INTERACTIVE_CELL_CLASSNAME}`,
      renderCell: (item) => (
        <Checkbox
          checked={selectedIds.has(item.id)}
          onCheckedChange={(v) => onCheckedChange(item.id, v === true)}
          aria-label={t('workspace.unitsTab.checkboxAriaLabel', { id: item.id })}
          data-testid={`unit-row-checkbox-${item.id}`}
        />
      ),
    });
  }

  cols.push({
    // Server-computed identifier preserves the catalog display-number policy;
    // the shared renderer keeps its presentation identical to the catalog.
    id: 'identifier',
    label: t('workspace.unitsTab.columns.identifier'),
    align: 'left',
    width: 130,
    sortable: true,
    sortKey: 'grwId',
    renderCell: (item) => renderMediaUnitGrwId(item.identifier),
  });

  cols.push(...buildUnitsSharedColumns(t, canViewSupplierDetails));
  cols.push(...buildOfferUnitCostColumns(t));
  cols.push(...buildOfferUnitPriceColumns({ ...price, canEdit: canEdit && price.canEdit, t }));

  cols.push(
    {
      id: 'clientDecision',
      label: t('workspace.unitsTab.columns.clientDecision'),
      align: 'left',
      width: 130,
      sortable: true,
      sortKey: 'clientDecision',
      renderCell: (item) => {
        const key =
          item.clientDecision == null
            ? 'workspace.unitsTab.decision.unavailable'
            : (
                {
                  ACCEPTED: 'workspace.unitsTab.decision.accepted',
                  REJECTED: 'workspace.unitsTab.decision.rejected',
                  UNDECIDED: 'workspace.unitsTab.decision.undecided',
                  NOT_IN_VERSION: 'workspace.unitsTab.decision.notInVersion',
                } as const
              )[item.clientDecision];
        return <span className="text-[12px] text-muted-foreground">{t(key)}</span>;
      },
    },
    {
      id: 'note',
      label: t('workspace.unitsTab.columns.note'),
      align: 'left',
      // Every other column in this table (the field-visibility-registry
      // columns via buildUnitsSharedColumns, plus the ad hoc cost/price/
      // identifier/clientDecision columns) already carries an explicit
      // pixel width — this was the one exception. Under the virtualized row
      // model each `<tr>` is rendered with `display: table; table-layout:
      // fixed` (data-table-rows.tsx), so a column with NO explicit width
      // gets the table's fixed-layout "leftover space" share, which
      // resolves to 0px here because the row's own computed width already
      // equals the sum of every OTHER column's explicit width — collapsing
      // this column (and its input) to zero width/unclickable instead of
      // the flexible fill-remaining-space sizing it had in the pre-
      // virtualization normal-document-flow table. Matches the catalog's
      // own 'notes'/'locationDescription' columns (media-units/lib/
      // columns-extended.tsx), both width: 200.
      width: 200,
      sortable: true,
      sortKey: 'note',
      // INTERACTIVE_CELL_CLASSNAME ('relative z-[1]') so the inline note input keeps
      // winning hit-tests over the row-link stretched-link overlay.
      cellClassName: INTERACTIVE_CELL_CLASSNAME,
      renderCell: (item) => (
        <div data-testid={`unit-note-cell-${item.id}`}>
          <NoteInput
            unit={item}
            disabled={!canEdit}
            placeholder={t('workspace.unitsTab.noteInputPlaceholder')}
            ariaLabel={t('workspace.unitsTab.noteInputAriaLabel', { id: item.id })}
            onBlur={onNoteBlur}
          />
        </div>
      ),
    },
  );

  return cols;
}
